import { InformationCircleIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React from 'react'

import { Tooltip } from '@/components/ui/Tooltip'
import { formatDate } from '@/utils/formatters'

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

/**
 * The lines that answer "why can this person see that store?".
 *
 * There are two sources today: the person's own Microsoft groups, and the groups of the
 * people who report to them. A dealer reached both ways is credited to the groups, so
 * the reporting-tree line names dealers the person would lose if their reports moved.
 */
const sourceLines = ({ directReportCount, groupCount, viaDirectReports, viaGroups }) => {
  const lines = []

  if (viaGroups) lines.push(`${plural(viaGroups, 'dealer')} via ${plural(groupCount, 'group')}`)
  if (viaDirectReports) {
    lines.push(
      `${plural(viaDirectReports, 'dealer')} via ${plural(directReportCount, 'direct report')}`,
    )
  }

  return lines
}

const SOURCE_LABEL = {
  group: 'group',
  direct_report: 'direct report',
}

// The tooltip sizes itself, rather than inheriting the column's width.
//
// Popper positions the card but leaves it shrink-to-fit inside the cell it hangs off,
// so with no width of its own the card comes out exactly as wide as the Dealer Access
// column — narrow enough to wrap every dealer name onto two lines. What the card has to
// hold has nothing to do with how wide that column happens to be.
const PANEL = 'bg-charcoal-950 rounded-md p-2'
const NOTE_PANEL = `${PANEL} w-[260px]`
const LIST_PANEL = `${PANEL} max-h-[340px] w-[400px] overflow-y-auto`

/**
 *
 * DealerAccessCell
 *
 * WARP-939 — which dealers a WPC user reaches, and where that reach comes from.
 *
 * The count is in the row and the dealers are named on hover. An access audit asks about
 * a particular store, so the names have to be reachable — but a person can reach hundreds
 * of dealers, and a row deep enough to hold them would push every other row off the
 * screen. The table stays scannable; the answer is one hover away.
 */
const DealerAccessCell = ({ dealerAccess, role }) => {
  if (!dealerAccess) return <span className="text-charcoal-400">—</span>

  const { dealers, directReportsUpdatedAt, groupsUpdatedAt, scope, total } = dealerAccess

  // Executives and Read Only users reach every dealer from their role alone, so a total
  // taken from their group assignments would describe access they do not depend on — and
  // naming every dealer in the system would say nothing at all.
  if (scope === 'all') {
    return (
      <Tooltip
        content={
          <div className={NOTE_PANEL}>
            <p className="text-sm text-white">
              The {role} role reaches every dealer, whatever groups this person belongs to.
            </p>
          </div>
        }
        placement="top"
      >
        <span className="text-charcoal-900 cursor-default font-medium">All dealers</span>
      </Tooltip>
    )
  }

  if (!total) {
    return (
      <Tooltip
        content={
          <div className={NOTE_PANEL}>
            <p className="text-sm text-white">
              No Microsoft group grants this person access to a dealer.
            </p>
          </div>
        }
        placement="top"
      >
        <span className="text-charcoal-400 cursor-default">No dealers</span>
      </Tooltip>
    )
  }

  const named = dealers || []
  // Both timestamps come from Microsoft Graph and refresh separately. The older one is
  // what the whole breakdown is only as fresh as.
  const asOf = [groupsUpdatedAt, directReportsUpdatedAt].filter(Boolean).sort()[0]

  return (
    <Tooltip
      content={
        <div className={LIST_PANEL}>
          <ul className="text-sm text-white">
            {sourceLines(dealerAccess).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <ul className="mt-1.5 border-t border-white/20 pt-1.5 text-sm text-white">
            {named.map((dealer) => (
              <li key={dealer.name}>
                {dealer.name}
                <span className="text-charcoal-light"> · via {SOURCE_LABEL[dealer.source]}</span>
              </li>
            ))}
          </ul>
          {total > named.length && (
            <p className="text-charcoal-light mt-1 text-xs">
              and {total - named.length} more not listed
            </p>
          )}
          <p className="text-charcoal-light mt-1 text-xs">
            {asOf ? `From Microsoft as of ${formatDate(asOf)}` : 'Never synced from Microsoft.'}
          </p>
        </div>
      }
      placement="top"
    >
      <span className="text-charcoal-900 flex cursor-default items-center gap-1">
        {plural(total, 'dealer')}
        <InformationCircleIcon aria-hidden="true" className="text-charcoal-400 h-4 w-4 shrink-0" />
      </span>
    </Tooltip>
  )
}

DealerAccessCell.defaultProps = {
  dealerAccess: null,
  role: 'WPC',
}

DealerAccessCell.propTypes = {
  dealerAccess: PropTypes.shape({
    dealers: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        source: PropTypes.string,
      }),
    ),
    directReportCount: PropTypes.number,
    directReportsUpdatedAt: PropTypes.string,
    groupCount: PropTypes.number,
    groupsUpdatedAt: PropTypes.string,
    scope: PropTypes.string,
    total: PropTypes.number,
    viaDirectReports: PropTypes.number,
    viaGroups: PropTypes.number,
  }),
  role: PropTypes.string,
}

export default DealerAccessCell
